import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["codeInput", "digitInput", "hiddenCode", "zenFingerprint", "spinner", "timer", "countdown", "statusMessage", "timeoutFallback", "timeoutNotice", "rejectedNotice", "retryForm", "retryError", "retryButton"]
  static values = { statusUrl: String, mode: String, blikGroup: Number, przelewy24TypeIds: Array, stripeTypeIds: Array, paynowTypeIds: Array, payuTypeIds: Array, zenTypeIds: Array, zenCollectorId: String, retryUrl: String }

  connect() {
    if (this.modeValue === "polling") {
      this.startPolling()
      this.startCountdown()
      if (this.hasZenFingerprintTarget) this.collectZenFingerprint()
    } else {
      this.observePaymentTypeChanges()
    }
  }

  disconnect() {
    this.stopPolling()
    this.stopCountdown()
  }

  // --- Cart form mode: BLIK code input ---

  formatCode(event) {
    const input = event.target
    input.value = input.value.replace(/\D/g, "").slice(0, 6)
    this.syncHiddenCode()
    this.updateSubmitButton()
  }

  // Multi-input mode (cart_v2_blik): handle digit typing
  digitType(event) {
    const input = event.target
    const value = input.value.replace(/\D/g, "")

    // Paste / autofill (incl. the mobile keyboard clipboard suggestion) lands
    // the whole code in one box via an input event - spread it across boxes.
    if (value.length > 1) {
      this.fillDigits(value)
      return
    }

    if (value.length === 1) {
      input.value = value
      this.focusNextDigit(input)
    } else {
      input.value = ""
    }

    this.syncHiddenCode()
    this.updateSubmitButton()
  }

  // Multi-input mode: handle keydown for navigation
  digitKeydown(event) {
    const input = event.target
    const inputs = this.digitInputTargets
    const index = inputs.indexOf(input)

    switch (event.key) {
      case "Backspace":
        if (input.value === "" && index > 0) {
          event.preventDefault()
          inputs[index - 1].value = ""
          inputs[index - 1].focus()
          this.syncHiddenCode()
          this.updateSubmitButton()
        }
        break
      case "ArrowLeft":
        event.preventDefault()
        if (index > 0) inputs[index - 1].focus()
        break
      case "ArrowRight":
        event.preventDefault()
        if (index < inputs.length - 1) inputs[index + 1].focus()
        break
    }
  }

  // Multi-input mode: handle paste (desktop and mobile context-menu paste)
  digitPaste(event) {
    event.preventDefault()
    const pasted = (event.clipboardData || window.clipboardData).getData("text")
    this.fillDigits(pasted)
  }

  // Spread a string of digits across the boxes, left to right, then sync.
  // Shared by paste (paste event) and autofill/clipboard-suggestion (input event).
  fillDigits(raw) {
    const inputs = this.digitInputTargets
    const digits = (raw || "").replace(/\D/g, "").slice(0, inputs.length)

    inputs.forEach((input, i) => { input.value = digits.charAt(i) || "" })

    if (inputs.length > 0) {
      const focusIndex = Math.max(0, Math.min(digits.length, inputs.length) - 1)
      inputs[focusIndex].focus()
    }

    this.syncHiddenCode()
    this.updateSubmitButton()
  }

  // Multi-input mode: select content on focus for easy overwrite
  digitFocus(event) {
    event.target.select()
  }

  // Prevent change events from digit inputs bubbling to cart form (avoids AJAX refresh)
  digitChange(event) {
    event.stopImmediatePropagation()
  }

  focusNextDigit(current) {
    const inputs = this.digitInputTargets
    const index = inputs.indexOf(current)
    if (index < inputs.length - 1) {
      inputs[index + 1].focus()
    }
  }

  syncHiddenCode() {
    if (this.hasHiddenCodeTarget) {
      const code = this.digitInputTargets.map(i => i.value).join("")
      this.hiddenCodeTarget.value = code
    }
  }

  getBlikCode() {
    if (this.hasHiddenCodeTarget) {
      return this.hiddenCodeTarget.value
    }
    if (this.hasCodeInputTarget) {
      return this.codeInputTarget.value
    }
    return ""
  }

  observePaymentTypeChanges() {
    const form = document.getElementById("cart-form")
    if (!form) return

    form.addEventListener("change", (event) => {
      if (event.target.name === "order[tpay_payment_group]" || event.target.name === "order[payment_type_id]") {
        this.toggleBlikVisibility()
      }
    })

    form.addEventListener("submit", () => {
      this.disableSubmitOnBlikPayment()
    })

    this.toggleBlikVisibility()
  }

  toggleBlikVisibility() {
    // Wylaczony select = klient wybral kafel wyroznionej metody (np. PayPo).
    const groupSelect = document.querySelector("[name='order[tpay_payment_group]']:not(:disabled)")
    const selectedGroup = groupSelect ? parseInt(groupSelect.value) : null
    const isTpayBlik = selectedGroup === this.blikGroupValue

    const paymentTypeRadio = document.querySelector("[name='order[payment_type_id]']:checked")
    const selectedTypeId = paymentTypeRadio ? parseInt(paymentTypeRadio.value) : null
    const isPrzelewy24Blik = this.hasPrzelewy24TypeIdsValue &&
                             this.przelewy24TypeIdsValue.includes(selectedTypeId)
    const isStripeBlik = this.hasStripeTypeIdsValue &&
                         this.stripeTypeIdsValue.includes(selectedTypeId)
    const isPaynowBlik = this.hasPaynowTypeIdsValue &&
                         this.paynowTypeIdsValue.includes(selectedTypeId)
    const isPayuBlik = this.hasPayuTypeIdsValue &&
                       this.payuTypeIdsValue.includes(selectedTypeId)
    const isZenBlik = this.hasZenTypeIdsValue &&
                      this.zenTypeIdsValue.includes(selectedTypeId)

    const isBlik = isTpayBlik || isPrzelewy24Blik || isStripeBlik || isPaynowBlik || isPayuBlik || isZenBlik
    const blikSection = this.element

    if (isBlik) {
      blikSection.classList.remove("hidden")
      if (isZenBlik) this.collectZenFingerprint()
      this.updateSubmitButton()
    } else {
      blikSection.classList.add("hidden")
      this.resetSubmitButton()
      if (this.hasCodeInputTarget) {
        this.codeInputTarget.value = ""
      }
      if (this.hasHiddenCodeTarget) {
        this.hiddenCodeTarget.value = ""
        this.digitInputTargets.forEach(i => i.value = "")
      }
    }
  }

  // ZEN Web Collector laduje sie async - probujemy kilka razy, zanim klient
  // wpisze kod. Brak odcisku nie blokuje platnosci, ZEN sam oceni ryzyko.
  collectZenFingerprint(attempt = 0) {
    if (!this.hasZenFingerprintTarget || this.zenFingerprintTarget.value) return
    if (typeof window.getCollectorData !== "function") {
      if (attempt < 20) setTimeout(() => this.collectZenFingerprint(attempt + 1), 250)
      return
    }
    Promise.resolve(window.getCollectorData(this.zenCollectorIdValue))
      .then((fingerPrintId) => {
        if (fingerPrintId) this.zenFingerprintTarget.value = fingerPrintId
      })
      .catch(() => {})
  }

  updateSubmitButton() {
    const hasBlikCode = this.getBlikCode().length === 6
    const buttons = document.querySelectorAll("#submit-btn-text, #submit-btn-text-desktop")
    buttons.forEach((btn) => {
      if (hasBlikCode) {
        btn.textContent = "Zamów i zapłać"
      } else {
        btn.textContent = btn.dataset.originalText || btn.textContent
        if (!btn.dataset.originalText) {
          btn.dataset.originalText = btn.textContent
        }
      }
    })
  }

  disableSubmitOnBlikPayment() {
    const form = document.getElementById("cart-form")
    if (!form) return

    const blikCode = this.getBlikCode()
    if (blikCode.length !== 6) return

    const submitButtons = form.querySelectorAll("[type='submit']")
    submitButtons.forEach((btn) => {
      btn.disabled = true
      btn.style.opacity = "0.6"
      btn.style.cursor = "wait"
    })
  }

  resetSubmitButton() {
    const buttons = document.querySelectorAll("#submit-btn-text, #submit-btn-text-desktop")
    buttons.forEach((btn) => {
      if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText
      }
    })
  }

  // --- Polling mode: blik_pending page ---

  startPolling() {
    this.pollInterval = setInterval(() => this.checkStatus(), 2000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async checkStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()

      if (data.status === "verified") {
        this.stopPolling()
        this.stopCountdown()
        this.showSuccess()
        setTimeout(() => {
          window.location.href = data.redirect_url
        }, 1000)
      } else if (data.status === "rejected") {
        this.stopPolling()
        this.stopCountdown()
        this.showRejected()
      } else if (data.status === "error") {
        this.stopPolling()
        this.stopCountdown()
        this.showError("Wystąpił błąd płatności.")
      }
    } catch (e) {
      // Network error - keep polling
    }
  }

  startCountdown() {
    this.remainingSeconds = 120
    this.countdownInterval = setInterval(() => {
      this.remainingSeconds--
      if (this.hasCountdownTarget) {
        const minutes = Math.floor(this.remainingSeconds / 60)
        const seconds = this.remainingSeconds % 60
        this.countdownTarget.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`
      }
      if (this.remainingSeconds <= 0) {
        this.onTimeout()
      }
    }, 1000)
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  onTimeout() {
    this.stopPolling()
    this.stopCountdown()

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("animate-spin")
      this.spinnerTarget.classList.add("border-t-amber-400")
    }

    if (this.hasTimerTarget) {
      this.timerTarget.classList.add("hidden")
    }

    if (this.hasTimeoutFallbackTarget) {
      this.timeoutFallbackTarget.classList.remove("hidden")
    }
  }

  showRejected() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("animate-spin", "border-t-[var(--main-color)]")
      this.spinnerTarget.classList.add("border-t-red-500")
      this.spinnerTarget.style.animation = "none"
      this.spinnerTarget.style.borderTopColor = "#ef4444"
    }

    if (this.hasTimerTarget) {
      this.timerTarget.classList.add("hidden")
    }

    if (this.hasTimeoutNoticeTarget) this.timeoutNoticeTarget.classList.add("hidden")
    if (this.hasRejectedNoticeTarget) this.rejectedNoticeTarget.classList.remove("hidden")

    if (this.hasTimeoutFallbackTarget) {
      this.timeoutFallbackTarget.classList.remove("hidden")
    }
  }

  // Nowy kod BLIK na ekranie oczekiwania, po odrzuceniu albo wygasnieciu.
  async submitRetry(event) {
    event.preventDefault()
    const code = this.getBlikCode()
    if (code.length !== 6) {
      this.showRetryError("Wpisz 6 cyfr kodu BLIK.")
      return
    }

    this.retryButtonTarget.disabled = true
    this.hideRetryError()
    const tokenTag = document.querySelector("meta[name='csrf-token']")
    const token = tokenTag ? tokenTag.content : null
    const body = new URLSearchParams({ blik_code: code })
    if (this.hasZenFingerprintTarget && this.zenFingerprintTarget.value) {
      body.append("zen_fingerprint_id", this.zenFingerprintTarget.value)
    }

    try {
      const response = await fetch(this.retryUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": token || "" },
        body
      })
      const data = await response.json()
      if (data.status === "pending") {
        this.restartWaiting()
      } else {
        this.showRetryError(data.message || "Nie udało się wysłać kodu. Spróbuj ponownie.")
        if (data.retry_available === false && this.hasRetryFormTarget) {
          this.retryFormTarget.classList.add("hidden")
        }
      }
    } catch (e) {
      this.showRetryError("Brak połączenia. Spróbuj ponownie.")
    } finally {
      this.retryButtonTarget.disabled = false
    }
  }

  restartWaiting() {
    this.digitInputTargets.forEach(i => { i.value = "" })
    this.syncHiddenCode()

    if (this.hasTimeoutFallbackTarget) this.timeoutFallbackTarget.classList.add("hidden")
    if (this.hasRejectedNoticeTarget) this.rejectedNoticeTarget.classList.add("hidden")
    if (this.hasTimeoutNoticeTarget) this.timeoutNoticeTarget.classList.remove("hidden")
    if (this.hasStatusMessageTarget) this.statusMessageTarget.classList.add("hidden")
    if (this.hasTimerTarget) this.timerTarget.classList.remove("hidden")
    if (this.hasCountdownTarget) this.countdownTarget.textContent = "2:00"

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("border-t-red-500", "border-t-amber-400")
      this.spinnerTarget.style.animation = "blik-spin 1s linear infinite"
      this.spinnerTarget.style.borderTopColor = "var(--main-color)"
    }

    this.stopPolling()
    this.stopCountdown()
    this.startPolling()
    this.startCountdown()
  }

  showRetryError(message) {
    if (!this.hasRetryErrorTarget) return
    this.retryErrorTarget.textContent = message
    this.retryErrorTarget.classList.remove("hidden")
  }

  hideRetryError() {
    if (this.hasRetryErrorTarget) this.retryErrorTarget.classList.add("hidden")
  }

  showSuccess() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("animate-spin", "border-t-[var(--main-color)]")
      this.spinnerTarget.classList.add("border-t-green-500")
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.innerHTML = '<p class="text-green-600 font-medium">Płatność potwierdzona! Przekierowuję...</p>'
      this.statusMessageTarget.classList.remove("hidden")
    }

    if (this.hasTimerTarget) {
      this.timerTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("animate-spin", "border-t-[var(--main-color)]")
      this.spinnerTarget.classList.add("border-t-red-500")
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.innerHTML = `<p class="text-red-600 font-medium">${message}</p>`
      this.statusMessageTarget.classList.remove("hidden")
    }

    if (this.hasTimerTarget) {
      this.timerTarget.classList.add("hidden")
    }

    if (this.hasTimeoutFallbackTarget) {
      this.timeoutFallbackTarget.classList.remove("hidden")
    }
  }
}
